import axios from "axios";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

import { notifyUser } from "../utils/notifyUser";

const AddPeople = ({ user }) => {
    const navigate = useNavigate();
    const { groupId } = useParams();
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [alreadyAddedUsers, setAlreadyAddedUsers] = useState([]);
    const [groupData, setgroupData] = useState(null);
    const email = user?.email;

    // Load already assigned users
    useEffect(() => {
        const fetchAssignedUsers = async () => {
            if(!groupId) return;

            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/security-groups/${groupId}`,
                    { withCredentials: true },
                );
                const { added_people } = res.data;
                setgroupData(res.data);
                if(added_people) {
                    const ids = JSON.parse(added_people);
                    if(ids.length > 0) {
                        const userRes = await axios.get(
                            `${
                                process.env.REACT_APP_BACKEND_URL
                            }/api/users/by-ids?ids=${ids.join(",")}`,
                            { withCredentials: true },
                        );
                        setAlreadyAddedUsers(userRes.data);
                    }
                }
            } catch(error) {
                console.error("Error fetching assigned users:", error);
            }
        };

        fetchAssignedUsers();
    }, [groupId]);

    // Live search
    useEffect(() => {
        if(searchQuery.trim() === "") {
            setFilteredUsers([]);
            return;
        }

        const fetchUsers = async () => {
            try {
                const res = await axios.get(
                    `${
                        process.env.REACT_APP_BACKEND_URL
                    }/api/users/search?search=${searchQuery.trim()}`,
                    { withCredentials: true },
                );
                const users = res.data.filter(
                    (user) =>
                        user.role === "investor" ||
                        user.role === "entrepreneur" ||
                        user.role === "admin",
                );

                const available = users.filter(
                    (user) =>
                        !selectedUsers.some(
                            (selected) => selected.id === user.id,
                        ) &&
                        !alreadyAddedUsers.some(
                            (added) => added.id === user.id,
                        ),
                );
                setFilteredUsers(available);
            } catch(err) {
                console.error("Error fetching users", err);
                setFilteredUsers([]);
            }
        };

        fetchUsers();
    }, [searchQuery, selectedUsers, alreadyAddedUsers]);

    // Add user to selected list
    const addUser = async (user) => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/security-groups/check-people/${groupId}`,
                { peopleId: user.id },
                { withCredentials: true },
            );

            if(response.data.status) {
                notifyUser(null, "error", response.data.message, "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                setSelectedUsers([...selectedUsers, user]);
            }
        } catch(error) {
            console.error("Error checking person:", error);
        }
    };

    // Remove user from selected list
    const removeUser = (id) => {
        setSelectedUsers(selectedUsers.filter((user) => user.id !== id));
    };

    // Remove user from already added users
    const removeAlreadyAddedUser = async (userId) => {
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/security-groups/${groupId}/remove-user`,
                {
                    userId,
                    audit_updated_by: email,
                },
                { withCredentials: true },
            );

            if(response.data.success) {
                setAlreadyAddedUsers(
                    alreadyAddedUsers.filter((user) => user.id !== userId),
                );
                notifyUser(null, "success", "User removed from group.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                notifyUser(null, "error", "Failed to remove user from group.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(err) {
            console.error("Error removing user from group:", err);
            notifyUser(null, "error", "Error occurred while removing user.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Handle Done button click - Save all users
    const handleDone = async () => {
        if(!groupId || groupId === "null") {
            alert("Group ID missing.");
            return;
        }

        const combinedUsers = [...alreadyAddedUsers, ...selectedUsers];

        try {
            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/security-groups/${groupId}/addpeople`,
                {
                    added_people: JSON.stringify(
                        combinedUsers.map((user) => user.id),
                    ),
                    audit_updated_by: email,
                },
                { withCredentials: true },
            );

            // Move selected users to already added users
            setAlreadyAddedUsers([...alreadyAddedUsers, ...selectedUsers]);
            // Clear selected users
            setSelectedUsers([]);

            notifyUser(null, "success", "Users saved successfully!", "toast", null, null, null, null, null, false, false, "top-center");
        } catch(err) {
            console.error("Error saving users to group:", err);
            notifyUser(null, "error", "Failed to save users.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    return (
        <div className="container mx-auto p-6">
            {/* Breadcrumb Navigation */}
            <nav className="text-sm mb-4">
                <ul className="flex space-x-2 text-gray-600">
                    <li
                        className="cursor-pointer hover:text-blue-600"
                        onClick={() => navigate("/dashboard")}
                    >
                        Home
                    </li>
                    <li>/</li>
                    <li
                        className="cursor-pointer hover:text-blue-600"
                        onClick={() => navigate("/Security-Groups")}
                    >
                        Security Groups
                    </li>
                    <li>/</li>
                    <li className="font-semibold text-gray-800">
                        {groupData?.group_name}
                    </li>
                </ul>
            </nav>

            <h1 className="text-3xl font-bold mb-6 text-center text-gray-800">
                Update Security Group
            </h1>

            {/* Search Bar */}
            <input
                type="text"
                placeholder="Search by name, email, or role..."
                className="w-full border p-3 rounded-lg mb-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
            />

            {/* User Selection Section */}
            <div className="grid grid-cols-3 gap-4">
                {/* Already Added Users */}
                <div className="bg-gray-100 p-4 rounded-lg shadow">
                    <h2 className="text-lg font-semibold mb-2 text-gray-700">
                        Already Added Users
                    </h2>
                    <ul className="h-40 overflow-auto">
                        {alreadyAddedUsers.length > 0 ? (
                            alreadyAddedUsers.map((user) => (
                                <li
                                    key={user.id}
                                    className="flex justify-between items-center p-2 border-b bg-gray-200 rounded-md"
                                >
                                    <div>
                                        <div className="font-medium">
                                            {user.first_name} {user.last_name}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {user.email}
                                        </div>
                                        <div className="text-xs text-gray-400 capitalize">
                                            {user.role}
                                        </div>
                                    </div>
                                    <button
                                        className="bg-red-500 text-white px-3 py-1 rounded text-sm"
                                        onClick={() =>
                                            removeAlreadyAddedUser(user.id)
                                        }
                                    >
                                        Remove
                                    </button>
                                </li>
                            ))
                        ) : (
                            <p className="text-gray-500 text-sm">
                                No users added
                            </p>
                        )}
                    </ul>
                </div>

                {/* Available Users (Search Results) */}
                <div className="bg-white p-4 rounded-lg shadow">
                    <h2 className="text-lg font-semibold mb-2 text-gray-700">
                        Available Users
                    </h2>
                    <ul className="h-40 overflow-auto">
                        {filteredUsers.length > 0 ? (
                            filteredUsers.map((user) => (
                                <li
                                    key={user.id}
                                    className="flex justify-between items-center p-2 border-b hover:bg-gray-100 cursor-pointer rounded"
                                    onClick={() => addUser(user)}
                                >
                                    <div>
                                        <div className="font-medium">
                                            {user.first_name} {user.last_name}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {user.email}
                                        </div>
                                        <div className="text-xs text-gray-400 capitalize">
                                            {user.role}
                                        </div>
                                    </div>
                                    <button className="bg-green-500 text-white px-3 py-1 rounded text-sm">
                                        Add
                                    </button>
                                </li>
                            ))
                        ) : (
                            <p className="text-gray-500 text-sm">
                                No users found
                            </p>
                        )}
                    </ul>
                </div>

                {/* Selected Users */}
                <div className="bg-white p-4 rounded-lg shadow">
                    <h2 className="text-lg font-semibold mb-2 text-gray-700">
                        Selected Users
                    </h2>
                    <ul className="h-40 overflow-auto">
                        {selectedUsers.length > 0 ? (
                            selectedUsers.map((user) => (
                                <li
                                    key={user.id}
                                    className="flex justify-between items-center p-2 border-b bg-gray-200 rounded-md"
                                >
                                    <div>
                                        <div className="font-medium">
                                            {user.first_name} {user.last_name}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {user.email}
                                        </div>
                                        <div className="text-xs text-gray-400 capitalize">
                                            {user.role}
                                        </div>
                                    </div>
                                    <button
                                        className="bg-red-500 text-white px-3 py-1 rounded text-sm"
                                        onClick={() => removeUser(user.id)}
                                    >
                                        Remove
                                    </button>
                                </li>
                            ))
                        ) : (
                            <p className="text-gray-500 text-sm">
                                No users selected
                            </p>
                        )}
                    </ul>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between mt-6">
                <button
                    className="bg-gray-500 text-white px-5 py-2 rounded-md hover:bg-gray-700"
                    onClick={() => navigate("/security-Groups")}
                >
                    Back
                </button>
                <button
                    className="aqi-primary-action rounded-md bg-blue-600 px-5 py-2 text-white hover:bg-blue-700"
                    onClick={handleDone}
                >
                    Save Changes
                </button>
            </div>
        </div>
    );
};

AddPeople.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
        role: PropTypes.string,
        first_name: PropTypes.string,
        last_name: PropTypes.string,
    }),
};
export default AddPeople;
